import { useEffect, useRef, useState } from 'react';
import { Icon } from '~/components/ui';
import { useLocale, useT } from '~/i18n';
import { replyErrorMessage } from '../lib/threads';

/** El mismo tope que valida el backend (MAX_REPLY_CHARS). */
export const MAX_REPLY_CHARS = 10_000;

interface Props {
  /**
   * Resuelve cuando el correo SALIÓ. Si rechaza, el texto se queda escrito.
   * `replyId` es el mismo en cada reintento hasta que un envío se confirma: el
   * servidor lo usa para no mandar dos veces el mismo correo.
   */
  onSend: (text: string, replyId: string) => Promise<void>;
  /** A quién le va a llegar, para que nadie responda a ciegas. */
  to?: string;
  disabled?: boolean;
  /**
   * Texto que llega de afuera (la respuesta sugerida). Reemplaza lo escrito y
   * deja el foco en la caja; NO envía. `nonce` existe para que elegir dos veces
   * el mismo borrador (después de haberlo editado) lo vuelva a poner.
   */
  prefill?: { text: string; nonce: number } | null;
}

/**
 * El compositor de la respuesta (canal domain).
 *
 * La regla que gobierna los estados: mientras el envío no se confirme, el texto
 * NO se borra. El 502 del backend significa literalmente "el correo no salió y
 * no se guardó nada", así que vaciar el campo al fallar sería tirar lo que la
 * persona escribió. Se limpia sólo después de un 200.
 */
/** Un id por envío. `randomUUID` existe en todo navegador con contexto seguro. */
function newReplyId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export default function ReplyComposer({ onSend, to, disabled, prefill }: Props) {
  const t = useT('workspace').composer;
  const locale = useLocale();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  // El error crudo, no la frase: se traduce al pintar, así cambia con el idioma.
  const [error, setError] = useState<unknown>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  // Vive hasta que un envío se confirma: si la red se cortó y la persona
  // reintenta, el servidor reconoce el mismo envío en vez de mandar otro.
  const replyIdRef = useRef<string | null>(null);

  // El borrador que llega de afuera se aplica DURANTE el render, comparando el
  // nonce (el patrón de "ajustar estado cuando cambia una prop" de React): sin
  // efecto que pinte primero el texto viejo. Sólo el nonce manda: re-aplicarlo
  // por otra causa pisaría lo que la persona ya editó.
  const [appliedNonce, setAppliedNonce] = useState<number | null>(null);
  if (prefill && prefill.nonce !== appliedNonce) {
    setAppliedNonce(prefill.nonce);
    setText(prefill.text);
    setError(null);
  }

  // El foco sí es un efecto: toca el DOM, no el estado.
  useEffect(() => {
    if (prefill) areaRef.current?.focus();
  }, [prefill?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const trimmed = text.trim();
  const tooLong = trimmed.length > MAX_REPLY_CHARS;
  const canSend = !!trimmed && !tooLong && !sending && !disabled;

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const replyId = (replyIdRef.current ??= newReplyId());
      await onSend(trimmed, replyId);
      replyIdRef.current = null;
      setText('');
      areaRef.current?.focus();
    } catch (err) {
      setError(err ?? new Error('unknown'));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter escribe un salto de línea: esto es un correo, no un chat. Enviar es
    // un gesto explícito, con la combinación que ya usa cualquier cliente.
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <div className="composer">
      <textarea
        ref={areaRef}
        className={`textarea w-full ${tooLong ? 'textarea-error' : ''}`}
        rows={4}
        aria-label={t.label}
        placeholder={t.placeholder}
        value={text}
        disabled={sending || disabled}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={onKeyDown}
      />

      {error != null && (
        <div className="alert alert-error text-sm" role="alert">
          <span>{replyErrorMessage(error, locale)}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => void submit()} disabled={sending}>
            {t.retry}
          </button>
        </div>
      )}

      <div className="composer__row">
        <span className="composer__hint">
          {tooLong ? (
            <span className="text-friction">{t.tooLong(trimmed.length - MAX_REPLY_CHARS)}</span>
          ) : to ? (
            <>{t.to(to)}</>
          ) : (
            <>{t.shortcut}</>
          )}
        </span>
        <button className="btn btn-primary btn-sm gap-2" onClick={() => void submit()} disabled={!canSend}>
          {sending ? <span className="loading loading-spinner loading-xs" /> : <Icon name="send" />}
          {sending ? t.sending : t.send}
        </button>
      </div>
    </div>
  );
}
